import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) =>
  api.post("/auth/login", { email, password });
export const getMe = () => api.get("/auth/me");
export const changePassword = (currentPassword, newPassword) =>
  api.post("/auth/change-password", { currentPassword, newPassword });

// Users
export const getUsers = () => api.get("/users");
export const getSellers = () => api.get("/users/sellers");
export const createUser = (data) => api.post("/users", data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Statuses
export const getStatuses = () => api.get("/statuses");
export const createStatus = (data) => api.post("/statuses", data);
export const updateStatus = (id, data) => api.put(`/statuses/${id}`, data);
export const deleteStatus = (id) => api.delete(`/statuses/${id}`);
export const reorderStatuses = (statusIds) => api.post("/statuses/reorder", { statusIds });

// Campaigns
export const getCampaigns = () => api.get("/campaigns");
export const getCampaign = (id) => api.get(`/campaigns/${id}`);
export const createCampaign = (data) => api.post("/campaigns", data);
export const updateCampaign = (id, data) => api.put(`/campaigns/${id}`, data);
export const deleteCampaign = (id, force = false) =>
  api.delete(`/campaigns/${id}?force=${force}`);
export const getCampaignStats = (id) => api.get(`/campaigns/${id}/stats`);

// Sources
export const getSources = () => api.get("/sources");
export const createSource = (data) => api.post("/sources", data);
export const updateSource = (id, data) => api.put(`/sources/${id}`, data);
export const deleteSource = (id) => api.delete(`/sources/${id}`);
export const getSourceStats = (id) => api.get(`/sources/${id}/stats`);

// Leads
export const getLeads = (params) => api.get("/leads", { params });
export const getLeadsCount = (params) => api.get("/leads/count", { params });
export const getLead = (id) => api.get(`/leads/${id}`);
export const createLead = (data) => api.post("/leads", data);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);
export const bulkAssignLeads = (leadIds, sellerId, resetToNewStatus = false) =>
  api.post("/leads/bulk-assign", { leadIds, sellerId, resetToNewStatus });
export const bulkAutoAssignLeads = (leadIds, sellerIds, resetToNewStatus = false, shuffle = false) =>
  api.post("/leads/bulk-auto-assign", { leadIds, sellerIds, resetToNewStatus, shuffle });
export const bulkDeleteLeads = (leadIds) =>
  api.post("/leads/bulk-delete", { leadIds });
export const roundRobinAssign = () => api.post("/leads/round-robin");
export const exportLeadsCsv = (params) =>
  api.get("/leads/export/csv", { params, responseType: "blob" });

// Lead Notes
export const getLeadNotes = (leadId) => api.get(`/leads/${leadId}/notes`);
export const createLeadNote = (leadId, data) =>
  api.post(`/leads/${leadId}/notes`, data);
export const updateLeadNote = (leadId, noteId, data) =>
  api.put(`/leads/${leadId}/notes/${noteId}`, data);
export const deleteLeadNote = (leadId, noteId) =>
  api.delete(`/leads/${leadId}/notes/${noteId}`);

// Lead Marker
export const toggleLeadMarker = (leadId) => api.post(`/leads/${leadId}/toggle-marker`);
export const clearAllMarkers = () => api.post("/leads/clear-markers");

// Lead History
export const getLeadHistory = (leadId) => api.get(`/leads/${leadId}/history`);

// Import
export const previewCsv = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/import/preview", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const checkCsvDuplicates = (file, mappingConfig) => {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams();
  params.append("mappingConfig", JSON.stringify(mappingConfig));
  return api.post(`/import/check-duplicates?${params.toString()}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const executeCsvImport = (
  file,
  mappingConfig,
  campaignId,
  sourceId,
  defaultStatusId,
  globalDuplicateAction = "skip",
  duplicateResolutions = null,
  sellerIds = null,
  shuffleAssignment = false
) => {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams();
  params.append("mappingConfig", JSON.stringify(mappingConfig));
  params.append("campaignId", campaignId);
  params.append("globalDuplicateAction", globalDuplicateAction);
  if (sourceId) params.append("sourceId", sourceId);
  if (defaultStatusId) params.append("defaultStatusId", defaultStatusId);
  if (duplicateResolutions) {
    params.append("duplicateResolutions", JSON.stringify(duplicateResolutions));
  }
  if (sellerIds && sellerIds.length > 0) {
    params.append("sellerIds", JSON.stringify(sellerIds));
    params.append("shuffleAssignment", shuffleAssignment.toString());
  }
  return api.post(`/import/execute?${params.toString()}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000, // 5 minutes timeout for large imports
  });
};

export const getImportJobs = () => api.get("/import/jobs");

// Audit Logs
export const getAuditLogs = (params) => api.get("/audit-logs", { params });

// Duplicates
export const getDuplicates = (params) => api.get("/duplicates", { params });
export const getDuplicateStats = () => api.get("/duplicates/stats");
export const getDuplicate = (id) => api.get(`/duplicates/${id}`);
export const resolveDuplicate = (id, resolution) => 
  api.put(`/duplicates/${id}/resolve?resolution=${resolution}`);
export const deleteDuplicate = (id) => api.delete(`/duplicates/${id}`);
export const bulkResolveDuplicates = (ids, resolution) =>
  api.post(`/duplicates/bulk-resolve?resolution=${resolution}`, ids);

// Dashboard
export const getDashboardStats = () => api.get("/dashboard/stats");

// Seed
export const seedDatabase = () => api.post("/seed");

// ============== EMAIL ==============
// Email Settings
export const getEmailSettings = () => api.get("/email/settings");
export const setEmailMode = (useGlobal) => api.post(`/email/settings/mode?use_global=${useGlobal}`);

// Global Email Config (Admin)
export const getGlobalEmailConfig = () => api.get("/email/config/global");
export const saveGlobalEmailConfig = (config) => api.post("/email/config/global", config);

// User Email Config
export const getUserEmailConfig = () => api.get("/email/config/user");
export const saveUserEmailConfig = (config) => api.post("/email/config/user", config);
export const testEmailConfig = (config) => api.post("/email/config/test", config);

// Email Templates
export const getEmailTemplates = () => api.get("/email/templates");
export const getEmailTemplate = (id) => api.get(`/email/templates/${id}`);
export const createEmailTemplate = (template) => api.post("/email/templates", template);
export const updateEmailTemplate = (id, template) => api.put(`/email/templates/${id}`, template);
export const deleteEmailTemplate = (id) => api.delete(`/email/templates/${id}`);

// Send Email
export const sendEmailToLead = (data) => api.post("/email/send", data);
export const getEmailHistory = (params) => api.get("/email/history", { params });
export const getLeadEmailHistory = (leadId) => api.get(`/email/history/lead/${leadId}`);

// LeadFlow Integration
export const getLeadFlowSettings = () => api.get("/leadflow/settings");
export const regenerateLeadFlowKey = () => api.post("/leadflow/regenerate-key");

export default api;
